import { useMemo, useState } from 'react';
import type { AccountOption } from '../hooks/useAssetAccounts';
import { MAINTENANCE_GROUP_VALUES } from '../monitoringUtils';
import { UptimeDrawer } from '../../shared';
import { getMaintenanceGroupName } from './maintenanceGroupName';
import useFmText from './useFmText';
import './MoreFiltersDrawer.scss';

interface IMoreFiltersDrawerProps {
    showAccounts: boolean;
    accounts: AccountOption[];
    accountsLoading: boolean;
    selectedAccounts: string[];
    selectedMaintenanceGroups: string[];
    onApply: (accountKeys: string[], maintenanceGroups: string[]) => void;
    onClose: () => void;
}

const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter(item => item !== value) : [...list, value];

const MoreFiltersDrawer = ({
    showAccounts,
    accounts,
    accountsLoading,
    selectedAccounts,
    selectedMaintenanceGroups,
    onApply,
    onClose,
}: IMoreFiltersDrawerProps) => {
    const { t, formatNumber } = useFmText();
    const [selected, setSelected] = useState(selectedAccounts);
    const [maintenanceGroups, setMaintenanceGroups] = useState(selectedMaintenanceGroups);
    const [search, setSearch] = useState('');

    const visibleAccounts = useMemo(() => {
        const term = search.trim().toLowerCase();
        return term ? accounts.filter(account => account.name.toLowerCase().includes(term)) : accounts;
    }, [accounts, search]);

    return (
        <UptimeDrawer
            className='fleet-monitoring'
            title={t('filters.more')}
            onClose={onClose}
            footer={
                <>
                    <button
                        type='button'
                        className='fm-btn'
                        onClick={() => {
                            setSelected([]);
                            setMaintenanceGroups([]);
                            setSearch('');
                        }}
                    >
                        {t('moreFilters.clear')}
                    </button>
                    <button
                        type='button'
                        className='fm-btn fm-primary'
                        onClick={() => onApply(selected, maintenanceGroups)}
                    >
                        {t('moreFilters.apply')}
                    </button>
                </>
            }
        >
            {showAccounts && (
                <div className='fm-fgroup'>
                    <span>{t('filters.account')}</span>
                    <input
                        type='text'
                        value={search}
                        placeholder={t('moreFilters.searchAccount')}
                        aria-label={t('moreFilters.searchAccount')}
                        onChange={event => setSearch(event.target.value)}
                    />
                    <div className='fm-checklist'>
                        {visibleAccounts.length ? (
                            visibleAccounts.map(account => (
                                <label key={account.key}>
                                    <input
                                        type='checkbox'
                                        checked={selected.includes(account.key)}
                                        onChange={() => setSelected(prev => toggle(prev, account.key))}
                                    />
                                    {account.name}
                                    <em className='fm-num'>{formatNumber(account.count)}</em>
                                </label>
                            ))
                        ) : (
                            <p className='fm-muted fm-checklist__empty'>
                                {accountsLoading ? t('moreFilters.loadingAccounts') : t('moreFilters.noAccount')}
                            </p>
                        )}
                    </div>
                    <small className='fm-muted'>
                        {accountsLoading && visibleAccounts.length
                            ? t('moreFilters.loadingAccounts')
                            : t('moreFilters.accountHint')}
                    </small>
                </div>
            )}

            <div className='fm-fgroup'>
                <span>{t('filters.maintenanceGroup')}</span>
                <div className='fm-pills'>
                    {[...MAINTENANCE_GROUP_VALUES, '-'].map(value => (
                        <button
                            type='button'
                            key={value}
                            className='fm-pillbtn'
                            aria-pressed={maintenanceGroups.includes(value)}
                            onClick={() => setMaintenanceGroups(prev => toggle(prev, value))}
                        >
                            {getMaintenanceGroupName(t, value)}
                        </button>
                    ))}
                </div>
            </div>
        </UptimeDrawer>
    );
};

export default MoreFiltersDrawer;
